import type { DealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import { fixingIndexMappingRecordToMap, type FixingIndexMappingRecord } from '@src/utils/fixtures/OTC/fixingFeedUtils';
import type { Draft } from 'immer';
import type { DealProductBuilderState } from './dealProductBuilderSlice';
import { getSliceStrategyForRow } from './productDefinitions/getSliceStrategy';

const isLegValid = (
  rowData: DealProductBuilderRow,
  validationErrors: Record<string, string> | null,
  fixingIndexMapping?: FixingIndexMappingRecord
): boolean => {
  const hasRequiredFields = Boolean(rowData.baseAsset);
  const { sliceStrategy, currentRow } = getSliceStrategyForRow(rowData);
  const fixingIndexMappingMap = fixingIndexMappingRecordToMap(fixingIndexMapping);
  const otcFieldsResult = sliceStrategy.validateOTCFields(currentRow.otcFields, fixingIndexMappingMap);
  const otcFieldsValid = otcFieldsResult ? Object.keys(otcFieldsResult).length === 0 : false;
  const hasValidationErrors = validationErrors ? Object.keys(validationErrors).length > 0 : false;

  return hasRequiredFields && otcFieldsValid && !hasValidationErrors;
};

/**
 * Derives computed fields like instrument name.
 * Pure function - does not mutate state.
 */
const prepareRowForValidation = (
  rowData: Draft<[DealProductBuilderRow, ...DealProductBuilderRow[]]>,
  rowIndex: number
): DealProductBuilderRow => {
  const targetRow = rowData.at(rowIndex);

  if (!targetRow) {
    throw new Error(`Row at index ${rowIndex} does not exist`);
  }

  // Create a new object to ensure ag-grid detects the change
  const updatedRow = { ...targetRow };

  const { sliceStrategy, currentRow } = getSliceStrategyForRow(updatedRow);
  const productTemplate = sliceStrategy.buildNoSuffixInstrumentName(currentRow);

  updatedRow.productTemplate = productTemplate;

  return updatedRow;
};

/**
 * Validates a prepared row and returns validation results.
 * Pure function - does not mutate state.
 */
const validatePreparedRow = (
  preparedRow: DealProductBuilderRow,
  fixingIndexMapping?: FixingIndexMappingRecord
): {
  validationErrors: Record<string, string> | null;
  isFormValid: boolean;
  isLegValid: boolean;
} => {
  const { sliceStrategy, currentRow } = getSliceStrategyForRow(preparedRow);
  const fixingIndexMappingMap = fixingIndexMappingRecordToMap(fixingIndexMapping);
  const validationErrors = sliceStrategy.validateFormState(currentRow, fixingIndexMappingMap);
  const isFormValid = validationErrors ? Object.keys(validationErrors).length === 0 : false;
  const isLegValidResult = isLegValid(preparedRow, validationErrors, fixingIndexMapping);

  return { validationErrors, isFormValid, isLegValid: isLegValidResult };
};

/**
 * Applies validation results to Redux state.
 */
const applyValidationResults = (
  state: Draft<DealProductBuilderState>,
  rowIndex: number,
  preparedRow: DealProductBuilderRow,
  validationResults: {
    validationErrors: Record<string, string> | null;
    isFormValid: boolean;
    isLegValid: boolean;
  }
): void => {
  state.isFormValid = validationResults.isFormValid;
  state.validationErrors = validationResults.validationErrors;

  preparedRow.isLegValid = validationResults.isLegValid;
  state.rowData[rowIndex] = preparedRow;
};

/**
 * Validates and updates the product builder form state for a specific row.
 *
 * This function orchestrates the full validation flow:
 * 1. Prepares the row by deriving computed fields (instrument name)
 * 2. Validates the prepared row
 * 3. Applies the validation results to Redux state
 *
 * @param state - The Redux state (Draft from Immer)
 * @param rowData - The current row data array
 * @param rowIndex - The index of the row to validate
 */
export const validateAndUpdateProductBuilderFormState = (
  state: Draft<DealProductBuilderState>,
  rowData: Draft<[DealProductBuilderRow, ...DealProductBuilderRow[]]>,
  rowIndex: number
): void => {
  const preparedRow = prepareRowForValidation(rowData, rowIndex);
  const validationResults = validatePreparedRow(preparedRow, state.fixingIndexMapping);
  applyValidationResults(state, rowIndex, preparedRow, validationResults);
};

/**
 * Validates and updates the product builder form state for all rows.
 *
 * Currently only one row exists, but this function is necessary because fixings
 * load asynchronously. If a user fills in a row while fixingIndexMapping is
 * undefined, validation skips "pair has fixing" checks. When fixings arrive
 * later, this function revalidates with the new mapping to catch previously
 * missed errors, preventing stale validation state.
 *
 * @param state - The Redux state (Draft from Immer)
 */
export const validateAndUpdateAllRowsProductBuilderFormState = (state: Draft<DealProductBuilderState>): void => {
  const allValidationErrors: Record<string, string> = {};
  let allRowsValid = true;

  for (let rowIndex = 0; rowIndex < state.rowData.length; rowIndex++) {
    const preparedRow = prepareRowForValidation(state.rowData, rowIndex);
    const validationResults = validatePreparedRow(preparedRow, state.fixingIndexMapping);

    // Update row-specific state
    preparedRow.isLegValid = validationResults.isLegValid;
    state.rowData[rowIndex] = preparedRow;

    // Aggregate validation errors
    if (validationResults.validationErrors) {
      Object.assign(allValidationErrors, validationResults.validationErrors);
    }

    // Form is valid only if all rows are valid
    if (!validationResults.isFormValid || !validationResults.isLegValid) {
      allRowsValid = false;
    }
  }

  // Update global validation state
  state.isFormValid = allRowsValid && Object.keys(allValidationErrors).length === 0;
  state.validationErrors = Object.keys(allValidationErrors).length > 0 ? allValidationErrors : null;
};
